import React, { Component, PropsWithChildren, Fragment } from 'react';
import './BlockUi.scss';
import Overlay from './Overlay';
import { IBlockProps } from './types';

/**
 * BlockElement hovers its children with blocking overlay and prevents any interaction inside.
 * It doesn't restore focus back to previously focused element since user is
 * free to tab and click around while this component is in the blocking mode.
 */
export class BlockElement extends Component<PropsWithChildren<IBlockProps>, {}> {
  // blocking container
  private node: HTMLElement | null;

  // blocking overlay
  private overlay: HTMLElement | null;

  // remember scrollY right before we change focus to prevent undesired scroll
  private scrollPosition: number;

  public componentDidMount() {
    window.document.addEventListener('keydown', this.onKeyDown);
    if (this.props.blocking) {
      this.focusOverlay();
    }
  }

  public componentWillUnmount() {
    window.document.removeEventListener('keydown', this.onKeyDown);
  }

  public componentDidUpdate(prevProps: IBlockProps) {
    if (this.props.blocking && !prevProps.blocking) {
      this.scrollPosition = window.pageYOffset;
      this.focusOverlay();
    }
  }

  public render() {
    const styles = this.props.blocking ? { position: 'relative' } : undefined;
    return (
      <Fragment>
        <div
          ref={this.setNodeRef}
          style={styles as React.CSSProperties}
          aria-live={'polite'}
          aria-busy={this.props.blocking}
          tabIndex={this.props.blocking ? 0 : -1}
          className={'hlc-blockui-busy'}
        >
          {this.props.children}
          {this.props.blocking && <Overlay zIndex={this.props.zIndex} setRef={this.setOverlayRef} />}
        </div>
      </Fragment>
    );
  }

  private focusOverlay() {
    const lastFocused = document.activeElement;
    if (this.node && this.node.contains(lastFocused)) {
      this.node.focus();
      window.scrollTo(0, this.scrollPosition);
    }
  }

  private setNodeRef = (ref: HTMLElement | null) => {
    this.node = ref;
  };
  private setOverlayRef = (ref: HTMLElement | null) => {
    this.overlay = ref;
  };

  // prevent tabbing inside
  private onKeyDown = (e: KeyboardEvent) => {
    if (!this.props.blocking || !this.node) {
      return;
    }
    if (e.keyCode !== 9) {
      return;
    }

    if (e.target === this.node && !e.shiftKey && this.overlay) {
      this.overlay.focus();
    }
    if (e.target === this.overlay && e.shiftKey) {
      this.node.focus();
    }
  };
}

export default BlockElement;
